import { useState } from 'react';
import XLSX from 'xlsx';
import { doSwaggerCall } from './useSwaggerClient';
import { excelExportSchemas } from '../tools/excelExportSchemas';

const useExcelExport = () => {
    const [isLoading, setIsLoading] = useState(false);

    const grabAllData = async ({ api, operation, apiResponseKey, total = 100, params }) => {
        const PAGE_LIMIT = 100;
        const allData = [];

        if (total <= PAGE_LIMIT) {
            const response = await doSwaggerCall(api, operation, {
                limit: total,
                offset: 0,
                ...params
            });

            return response[apiResponseKey] || [];
        }

        let offset = 0;
        while (offset < total) {
            // eslint-disable-next-line no-await-in-loop
            const response = await doSwaggerCall(api, operation, {
                limit: PAGE_LIMIT,
                offset,
                ...params
            });

            allData.push(...(response[apiResponseKey] || []));
            if (!response[apiResponseKey] || response[apiResponseKey].length < PAGE_LIMIT) {
                break;
            }
            offset += PAGE_LIMIT;
        }

        return allData;
    };

    const handleExport = async ({ total, variant, params }) => {
        if (!excelExportSchemas[variant]) {
            console.error('Invalid export variant:', variant);
            return;
        }
        const {
            sheetName,
            header,
            api,
            operation,
            filenamePrefix,
            apiResponseKey,
            mapper
        } = excelExportSchemas[variant];

        setIsLoading(true);
        try {
            const allData = await grabAllData({
                api,
                operation,
                params,
                total,
                apiResponseKey
            });

            const workbook = XLSX.utils.book_new();

            const mappedData = allData.map(data => mapper(data));

            const data = [header, ...mappedData];
            const worksheet = XLSX.utils.aoa_to_sheet(data);
            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
            // Check Workbook data before writeFile
            if (
                Object.keys(workbook.Sheets).length === 0 ||
                Object.values(workbook.Sheets).every(
                    sheet => !sheet || Object.keys(sheet).length === 0
                )
            ) {
                throw new Error('No valid data found for export');
            } else {
                // Generate filename with timestamp
                const timestamp = new Date()
                    .toISOString()
                    .replace(/[-:TZ.]/g, '')
                    .slice(0, 14);
                const filename = `${timestamp}_${filenamePrefix}.xlsx`;
                XLSX.writeFile(workbook, filename);
            }
        } catch (error) {
            console.error('Error exporting payments:', error);
            setIsLoading(false);
            throw error;
        }
        setIsLoading(false);
    };

    return { isLoading, handleExport };
};

export default useExcelExport;
